import React, { MouseEvent, useState } from 'react';

import classNames from 'classnames';

import MarkInfoIcon from '@uikit/icons/MarkInfo';

import { REWARD_POOL_STATUS, REWARD_POOL_TAG, RewardPool } from '@shared/api/types/rewardPools';
import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import NextLink from '@shared/common/components/NextLink';
import useRerenderAfterDate from '@shared/hooks/useRerenderAfterDate';

import useCheckRequiredSelfData from '@store:web/utils/hooks/useCheckRequiredSelfData';

import JoinRewardPoolButton from '../../RewardPoolsPage/RewardPoolCard/JoinRewardPoolButton';
import RewardPoolCardParticipationTag from './RewardPoolCardParticipationTag';
import RewardPoolCardRewards from './RewardPoolCardRewards';
import RewardPoolCardTags from './RewardPoolCardTags';
import RewardPoolCardWhitelistRequirementsDialog from './RewardPoolCardWhitelistRequirementsDialog';

interface RewardPoolsCardProps {
  rewardPool: RewardPool;
  className?: string;
}

const getPoolCardClassNames = (
  rewardPool: RewardPool,
): { rootClassName: string; bodyClassName: string } => {
  const isFeatured = rewardPool.tags?.includes(REWARD_POOL_TAG.FEATURED);
  const isLastChance = rewardPool.tags?.includes(REWARD_POOL_TAG.LAST_CHANCE);
  const isHot = rewardPool.tags?.includes(REWARD_POOL_TAG.HOT);
  const isHugeReward = rewardPool.tags?.includes(REWARD_POOL_TAG.HUGE_REWARD);
  const isTrending = rewardPool.tags?.includes(REWARD_POOL_TAG.TRENDING);

  if (isFeatured) {
    return {
      rootClassName: 'border-[#C8D8F9]',
      bodyClassName: 'to-[#F6F9FE]',
    };
  } else if (isTrending) {
    return {
      rootClassName: 'border-[#ABEDB6]',
      bodyClassName: 'to-[#EEFBF0]',
    };
  } else if (isHugeReward) {
    return {
      rootClassName: 'border-[#F9E2B4]',
      bodyClassName: 'to-[#FDF8EC]',
    };
  } else if (isLastChance) {
    return {
      rootClassName: 'border-[#F0C9F7]',
      bodyClassName: 'to-[#FCF6FE]',
    };
  } else if (isHot) {
    return {
      rootClassName: 'border-[#F7CDC9]',
      bodyClassName: 'to-[#FEF7F6]',
    };
  }

  return {
    rootClassName: 'border-[#E7E7EF]',
    bodyClassName: 'to-[#fff]',
  };
};

export const rewardPoolCardHeightClassName = 'min-h-[25rem] xl:min-h-[26.75rem]';

export const rewardPoolCardRootBaseClassName = `rounded-3xl ${rewardPoolCardHeightClassName}`;

const RewardPoolCard = ({ rewardPool, className }: RewardPoolsCardProps) => {
  const styleClassNames = getPoolCardClassNames(rewardPool);

  const { check } = useCheckRequiredSelfData();

  const [isOpen, setIsOpen] = useState(false);

  // NOTE: rerender component because some parts of ui depends on those dates
  useRerenderAfterDate(rewardPool.start_date);
  useRerenderAfterDate(rewardPool.end_date);

  const handleRequirementsButtonClick = () => {
    if (
      !check({
        auth: true,
      })
    ) {
      return;
    }

    setIsOpen(true);
  };

  const handleViewDetailsLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      !check({
        auth: true,
      })
    ) {
      event.preventDefault();
    }
  };

  return (
    <>
      <RewardPoolCardWhitelistRequirementsDialog
        rewardPool={rewardPool}
        onOpenChange={setIsOpen}
        open={isOpen}
      />

      <div
        className={classNames(
          'flex flex-col border-2 overflow-hidden',
          rewardPoolCardRootBaseClassName,
          styleClassNames.rootClassName,
          className,
        )}
        data-qa={`pool_id-${rewardPool.id}`}
      >
        <div className="h-[7.5rem] px-1.5 overflow-auto flex items-center bg-gradient-to-b from-[rgba(255,128,102,0.16)] via-[rgba(229,102,255,0.16)] to-[rgba(102,178,255,0.16)]">
          <RewardPoolCardRewards rewardPool={rewardPool} />
        </div>

        <div
          className={classNames(
            'bg-gradient-to-b from-[#fff] flex flex-grow flex-col gap-4 p-4 xl:p-6',
            styleClassNames.bodyClassName,
          )}
        >
          <RewardPoolCardParticipationTag rewardPool={rewardPool} />

          <div className="flex gap-2 items-baseline">
            <p className="text-sm font-semibold h-10 flex-1 xl:text-base xl:h-12 line-clamp-2 text-[#09090B]">
              {rewardPool.name}
            </p>

            {!!rewardPool.description && (
              <BaseInfoTooltip
                icon={
                  <span className="flex text-[#C2C2D6] flex-shrink-0 items-center justify-center size-5 xl:size-6">
                    <MarkInfoIcon className="icon-xs" />
                  </span>
                }
                content={rewardPool.description}
              />
            )}
          </div>

          <RewardPoolCardTags rewardPool={rewardPool} />

          <div className="flex flex-wrap gap-3 mt-auto pt-1">
            <button
              onClick={handleRequirementsButtonClick}
              disabled={
                rewardPool.status !== REWARD_POOL_STATUS.ACTIVE &&
                rewardPool.status !== REWARD_POOL_STATUS.PENDING
              }
              className="msq-btn msq-btn-lg msq-btn-secondary flex-1"
              data-qa="requirements_button"
            >
              Requirements
            </button>

            <NextLink
              onClick={handleViewDetailsLinkClick}
              href={`/reward-pools/${rewardPool.id}`}
              className="msq-btn msq-btn-md msq-btn-secondary flex-1"
              data-qa="details_button"
            >
              View Details
            </NextLink>

            <JoinRewardPoolButton rewardPool={rewardPool} />
          </div>
        </div>
      </div>
    </>
  );
};

export default RewardPoolCard;
